import { ApiError } from '@eneco/dc/client/ApiError';
import { checkInHouseAvailability } from '@eneco/dc/services/sales/EnecoInHouseService';
import { assign, setup, fromPromise } from 'xstate';

interface AvailabilityMachineContext {
  isAvailable?: boolean;
  errorMessage?: string;
}

interface AvailabilityMachineEvent {
  type: 'CHECK_ADDRESS';
  value: string;
  product: string;
  orderType: string;
}

interface InputType {
  context: AvailabilityMachineContext;
  event: AvailabilityMachineEvent;
}

interface OutputType {
  data: {
    isAvailable?: boolean;
  };
}

export const addressAvailabilityMachine = setup({
  types: {} as {
    context: AvailabilityMachineContext;
    events: AvailabilityMachineEvent;
    actors: {
      checkPostalAddress: {
        input: InputType;
        output: OutputType;
      };
    };
  },
  actors: {
    checkPostalAddress: fromPromise<OutputType, InputType>(({ input }) => {
      return new Promise((resolve, reject) => {
        checkInHouseAvailability({
          postalCode: input.event.value,
          product: input.event.product,
          orderType: input.event.orderType,
        })
          .then(response => {
            resolve({ data: response });
          })
          .catch((error: ApiError) => {
            reject(new Error(error?.message));
          });
      });
    }),
  },
}).createMachine({
  id: 'serviceAvailability',
  initial: 'IDLE',
  context: {},
  states: {
    IDLE: {
      on: {
        CHECK_ADDRESS: {
          target: 'FETCHING',
        },
      },
    },
    FETCHING: {
      entry: assign({
        isAvailable: undefined,
        errorMessage: undefined,
      }),
      invoke: {
        src: 'checkPostalAddress',
        input: ({ event, context }: { event: AvailabilityMachineEvent; context: AvailabilityMachineContext }) => ({
          event,
          context,
        }),
        onDone: {
          target: 'SUCCESS',
          actions: assign({
            isAvailable: ({ event }) => event.output?.data.isAvailable,
          }),
        },
        onError: {
          target: 'ERROR',
          actions: assign({
            errorMessage: ({ event }) => (event.error as Error).message,
          }),
        },
      },
    },
    SUCCESS: {
      on: {
        CHECK_ADDRESS: { target: 'FETCHING' },
      },
    },
    ERROR: {
      on: {
        CHECK_ADDRESS: { target: 'FETCHING' },
      },
    },
  },
});
